import { formatInteger } from '@brimo/shared';
import { Skeleton } from '../../../ds/components';
import type { Evaluation } from '../api';
import { Eyebrow, LoadError } from '../ui';
import styles from './session.module.css';

/**
 * Terminierung 11 — Queue pausiert · Session-Auswertung: six figures of the session, „Weiter wählen · N offen"
 * / „Queue beenden", the milestone in terracotta and the appointments of this session. An ended session shows
 * the same evaluation with the way back to the dialer.
 */
export function PauseView({
  evaluation,
  error,
  retrying,
  onRetry,
  busy,
  onResume,
  onEnd,
  onBack,
}: {
  evaluation: Evaluation | undefined;
  error: unknown;
  retrying: boolean;
  onRetry: () => void;
  busy: 'resume' | 'end' | null;
  onResume: () => void;
  onEnd: () => void;
  onBack: () => void;
}) {
  if (error) return <LoadError error={error} what="Die Auswertung" retrying={retrying} onRetry={onRetry} />;
  if (!evaluation) {
    return (
      <div className={styles.twoColumns} aria-busy="true">
        <div className={styles.pauseCard}>
          <Skeleton width={180} height={11} />
          <div className={styles.pauseTitle}>
            <Skeleton width={260} height={38} />
          </div>
        </div>
        <div className={styles.sideStack}>
          <Skeleton width="100%" height={120} />
        </div>
      </div>
    );
  }
  const ended = evaluation.status === 'ended';
  return (
    <div className={styles.twoColumns}>
      <section className={styles.pauseCard} aria-labelledby="dialer-pause-title">
        <Eyebrow>{evaluation.eyebrow}</Eyebrow>
        <h2 id="dialer-pause-title" className={styles.pauseTitle}>
          {ended ? 'Session' : 'Session bisher'}
        </h2>
        <dl className={styles.sessionFigures}>
          {evaluation.figures.map((figure) => (
            <div key={figure.key} className={styles.sessionFigure}>
              <dt>
                <Eyebrow>{figure.label}</Eyebrow>
              </dt>
              <dd className={styles.sessionFigureValue}>
                <span className={styles.sessionFigureNumber}>{figure.value}</span>
                {figure.sub ? <span className={styles.sessionFigureSub}>{figure.sub}</span> : null}
              </dd>
            </div>
          ))}
        </dl>
        <div className={styles.pauseCtas}>
          {ended ? (
            <button type="button" className={styles.cta} onClick={onBack}>
              Zum Dialer
            </button>
          ) : (
            <>
              <button
                type="button"
                className={styles.cta}
                disabled={busy !== null}
                aria-busy={busy === 'resume' || undefined}
                onClick={onResume}
              >
                Weiter wählen · {formatInteger(evaluation.openCount)} offen
              </button>
              <button
                type="button"
                className={styles.ctaSecondary}
                disabled={busy !== null}
                aria-busy={busy === 'end' || undefined}
                onClick={onEnd}
              >
                Queue beenden
              </button>
            </>
          )}
        </div>
      </section>
      <div className={styles.sideStack}>
        {evaluation.milestone ? (
          <section className={styles.milestone} aria-label="Meilenstein">
            <Eyebrow tone="accent">Meilenstein</Eyebrow>
            <div className={styles.milestoneTitle}>{evaluation.milestone.title}</div>
            {evaluation.milestone.text ? (
              <p className={styles.milestoneText}>{evaluation.milestone.text}</p>
            ) : null}
          </section>
        ) : null}
        <section className={styles.sideCard} aria-label="Termine aus dieser Session">
          <Eyebrow>Termine aus dieser Session</Eyebrow>
          <div className={styles.sessionAppointments}>
            {evaluation.appointments.length === 0 ? (
              <p className={styles.slotsNote}>Noch keine Termine in dieser Session.</p>
            ) : (
              evaluation.appointments.map((item) => (
                <div key={item.appointmentId} className={styles.sessionAppointment}>
                  <div className={styles.sessionAppointmentText}>
                    <div className={styles.sessionAppointmentName}>{item.name}</div>
                    {item.listName ? (
                      <div className={styles.sessionAppointmentList}>{item.listName}</div>
                    ) : null}
                  </div>
                  <div className={styles.sessionAppointmentWhen}>{item.when}</div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
